import { useState, useEffect } from 'react'
import { TargetDto } from '../services/api'
import LoadingSpinner from './common/LoadingSpinner'
import { createLogger } from '../utils/createLogger'

const log = createLogger('CampaignTargetSelector')

type SelectionMethod = 'all' | 'random' | 'department' | 'manual'

interface CampaignTargetSelectorProps {
  organisationId: string
  availableTargets: TargetDto[]
  departments: string[]
  loading?: boolean
  
  // Current selection state
  selectionMethod: SelectionMethod
  selectedEmails: string[]
  selectedDepartments: string[]
  sampleSize: number
  
  // Callbacks
  onSelectionMethodChange: (method: SelectionMethod) => void
  onSelectedEmailsChange: (emails: string[]) => void
  onSelectedDepartmentsChange: (departments: string[]) => void
  onSampleSizeChange: (size: number) => void
}

const CampaignTargetSelector: React.FC<CampaignTargetSelectorProps> = ({
  availableTargets,
  departments,
  loading = false,
  selectionMethod,
  selectedEmails,
  selectedDepartments,
  sampleSize,
  onSelectionMethodChange,
  onSelectedEmailsChange,
  onSelectedDepartmentsChange,
  onSampleSizeChange
}) => {
  const [searchQuery, setSearchQuery] = useState('')

  // Calculate counts
  const totalTargets = availableTargets.length
  const selectedCount = 
    selectionMethod === 'all' ? totalTargets :
    selectionMethod === 'random' ? sampleSize :
    selectionMethod === 'department' ? availableTargets.filter(t => 
      selectedDepartments.includes(t.department || '')
    ).length :
    selectedEmails.length

  // Filter targets for manual selection
  const filteredTargets = availableTargets.filter(target => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      target.target_email.toLowerCase().includes(query) ||
      target.given_name?.toLowerCase().includes(query) ||
      target.surname?.toLowerCase().includes(query) ||
      target.department?.toLowerCase().includes(query)
    )
  })

  // Toggle email selection
  const toggleEmail = (email: string) => {
    if (selectedEmails.includes(email)) {
      onSelectedEmailsChange(selectedEmails.filter(e => e !== email))
    } else {
      onSelectedEmailsChange([...selectedEmails, email])
    }
  }

  // Toggle department selection
  const toggleDepartment = (department: string) => {
    if (selectedDepartments.includes(department)) {
      onSelectedDepartmentsChange(selectedDepartments.filter(d => d !== department))
    } else {
      onSelectedDepartmentsChange([...selectedDepartments, department])
    }
  }

  // Select all/none for manual selection
  const selectAllFiltered = () => {
    const emails = filteredTargets.map(t => t.target_email)
    onSelectedEmailsChange([...new Set([...selectedEmails, ...emails])])
  }

  const clearSelection = () => {
    onSelectedEmailsChange([])
  }

  // Get department target count
  const getDepartmentCount = (department: string) => {
    return availableTargets.filter(t => t.department === department).length
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  if (totalTargets === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No targets in pool. Add targets in Settings to create campaigns.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Selection Method Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Target Selection Method
        </label>
        <select
          value={selectionMethod}
          onChange={(e) => {
            const method = e.target.value as SelectionMethod
            log.info('Dropdown changed', { from: selectionMethod, to: method })
            onSelectionMethodChange(method)
            
            // Reset method-specific state
            if (method !== 'manual') onSelectedEmailsChange([])
            if (method !== 'department') onSelectedDepartmentsChange([])
            if (method === 'random' && sampleSize === 0) onSampleSizeChange(1)
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All targets in pool</option>
          <option value="random">Random sample</option>
          {departments.length > 0 && <option value="department">By department</option>}
          <option value="manual">Manual selection</option>
        </select>
      </div>

      {/* Selection Count Summary */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          <span className="font-semibold">{selectedCount}</span> target{selectedCount !== 1 ? 's' : ''} selected
          {selectionMethod !== 'all' && <span className="text-blue-700 dark:text-blue-300"> out of {totalTargets} available</span>}
        </p>
      </div>

      {/* Method-Specific UI */}
      {selectionMethod === 'all' && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          All {totalTargets} targets in the pool will receive this campaign.
        </div>
      )}

      {selectionMethod === 'random' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Sample Size
            </label>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {sampleSize}
            </span>
          </div>
          <input
            type="range"
            min="1"
            max={totalTargets}
            value={sampleSize}
            onChange={(e) => onSampleSizeChange(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>1 target</span>
            <span>{totalTargets} targets</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            {sampleSize} {sampleSize === 1 ? 'target' : 'targets'} will be randomly selected when the campaign starts.
          </p>
        </div>
      )}

      {selectionMethod === 'department' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Departments
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-md divide-y divide-gray-200 dark:divide-gray-700 max-h-60 overflow-y-auto">
            {departments.map((dept) => {
              const count = getDepartmentCount(dept)
              const isSelected = selectedDepartments.includes(dept)
              
              return (
                <label
                  key={dept}
                  className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <div className="flex items-center flex-1">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleDepartment(dept)}
                      className="mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{dept}</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {count} {count === 1 ? 'target' : 'targets'}
                  </span>
                </label>
              )
            })}
          </div>
          {selectedDepartments.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Select at least one department
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onSelectedDepartmentsChange([...departments])}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Select all
            </button>
            <span className="text-gray-400">•</span>
            <button
              type="button"
              onClick={() => onSelectedDepartmentsChange([])}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {selectionMethod === 'manual' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Individual Targets
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="border border-gray-300 dark:border-gray-600 rounded-md max-h-80 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-10">
                    <input
                      type="checkbox"
                      checked={filteredTargets.length > 0 && filteredTargets.every(t => selectedEmails.includes(t.target_email))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllFiltered()
                        } else {
                          const filteredEmails = filteredTargets.map(t => t.target_email)
                          onSelectedEmailsChange(selectedEmails.filter(e => !filteredEmails.includes(e)))
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Email
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Name
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Department
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTargets.length > 0 ? (
                  filteredTargets.map((target) => {
                    const isSelected = selectedEmails.includes(target.target_email)
                    const fullName = [target.given_name, target.surname].filter(Boolean).join(' ') || '-'
                    
                    return (
                      <tr
                        key={target.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleEmail(target.target_email)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                          {target.target_email}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                          {fullName}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                          {target.department || '-'}
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No targets match your search' : 'No targets available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedEmails.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Select at least one target email
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAllFiltered}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Select all {searchQuery ? 'filtered' : ''}
            </button>
            <span className="text-gray-400">•</span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignTargetSelector
